import { useState } from "react";

const EMOJI_OPTIONS = [
  { emoji: "🙏", label: "Thanks" },
  { emoji: "❤️", label: "Love" },
  { emoji: "🌟", label: "Amazing" },
  { emoji: "👏", label: "Bravo" },
  { emoji: "🎉", label: "Awesome" },
  { emoji: "🍀", label: "Lucky" },
];

export default function KudosModal({ item, recipientType = "donor", isOpen, onClose, onSend, showDontAskAgain, onDisablePrompt }) {
  const [selectedEmoji, setSelectedEmoji] = useState("🙏");
  const [message, setMessage] = useState("");
  const [donorEmoji, setDonorEmoji] = useState("🙏");
  const [donorMessage, setDonorMessage] = useState("");
  const [courierEmoji, setCourierEmoji] = useState("🙏");
  const [courierMessage, setCourierMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [dontAskAgain, setDontAskAgain] = useState(false);

  if (!isOpen || !item) return null;

  const isBoth = recipientType === "both";
  const showDonor = isBoth ? !item.kudosSent : recipientType === "donor";
  const showCourier = isBoth && item.courierId && !item.courierKudosSent;
  const hasAnyToSend = showDonor || showCourier;

  const handleSend = async () => {
    setSending(true);
    if (isBoth) {
      await onSend(item.id, { donor: showDonor ? { emoji: donorEmoji, message: donorMessage.trim() } : null, courier: showCourier ? { emoji: courierEmoji, message: courierMessage.trim() } : null }, "both");
    } else {
      await onSend(item.id, { emoji: selectedEmoji, message: message.trim() }, recipientType);
    }
    setSending(false);
    setSent(true);
    if (dontAskAgain && onDisablePrompt) onDisablePrompt();
    setTimeout(() => {
      setSent(false);
      setSelectedEmoji("🙏");
      setMessage("");
      setDonorEmoji("🙏");
      setDonorMessage("");
      setCourierEmoji("🙏");
      setCourierMessage("");
      setDontAskAgain(false);
      onClose();
    }, 1500);
  };

  const handleClose = () => {
    if (dontAskAgain && onDisablePrompt) onDisablePrompt();
    setSelectedEmoji("🙏");
    setMessage("");
    setDonorEmoji("🙏");
    setDonorMessage("");
    setCourierEmoji("🙏");
    setCourierMessage("");
    setDontAskAgain(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div className="p-10 text-center">
            <div className="text-6xl mb-4 animate-bounce">🙏</div>
            <h3 className="text-xl font-bold text-gray-800 mb-1">Kudos Sent!</h3>
            <p className="text-sm text-gray-500">
              {isBoth && (showDonor || showCourier) ? "Your thanks have been shared" : recipientType === "courier" ? "The deliverer will appreciate your kindness" : "The donor will appreciate your kindness"}
            </p>
          </div>
        ) : (
          <>
            <div className="px-6 pt-6 pb-3 text-center">
              <h3 className="font-condensed text-2xl font-bold text-gray-800 uppercase tracking-wide">Send Kudos</h3>
              <p className="text-sm text-gray-500 mt-1">
                {isBoth ? (
                  <>Thank the <span className="font-semibold text-green-600">donor</span> and <span className="font-semibold text-green-600">deliverer</span> for "{item.name}"</>
                ) : recipientType === "courier" ? (
                  <>Thank your <span className="font-semibold text-green-600">deliverer</span> for bringing "{item.name}"</>
                ) : (
                  <>Thank <span className="font-semibold text-green-600">{item.name}</span>'s donor</>
                )}
              </p>
            </div>

            {isBoth && hasAnyToSend ? (
              <div className="px-6 pb-4 space-y-4">
                {showDonor && (
                  <div className="p-3 bg-amber-50 rounded-xl border border-amber-200/60">
                    <p className="text-xs font-semibold text-amber-800 mb-2">Thank the donor</p>
                    <div className="grid grid-cols-6 gap-1.5 mb-2">
                      {EMOJI_OPTIONS.map(({ emoji }) => (
                        <button key={emoji} onClick={() => setDonorEmoji(emoji)} className={`py-1.5 rounded-lg text-lg ${donorEmoji === emoji ? "bg-amber-200" : "bg-white hover:bg-amber-100"}`}>{emoji}</button>
                      ))}
                    </div>
                    <textarea value={donorMessage} onChange={(e) => setDonorMessage(e.target.value)} placeholder="Optional note for donor" maxLength={150} rows={1} className="w-full p-2 text-sm border border-amber-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-amber-400" />
                  </div>
                )}
                {showCourier && (
                  <div className="p-3 bg-blue-50 rounded-xl border border-blue-200/60">
                    <p className="text-xs font-semibold text-blue-800 mb-2">Thank the deliverer</p>
                    <div className="grid grid-cols-6 gap-1.5 mb-2">
                      {EMOJI_OPTIONS.map(({ emoji }) => (
                        <button key={emoji} onClick={() => setCourierEmoji(emoji)} className={`py-1.5 rounded-lg text-lg ${courierEmoji === emoji ? "bg-blue-200" : "bg-white hover:bg-blue-100"}`}>{emoji}</button>
                      ))}
                    </div>
                    <textarea value={courierMessage} onChange={(e) => setCourierMessage(e.target.value)} placeholder="Optional note for deliverer" maxLength={150} rows={1} className="w-full p-2 text-sm border border-blue-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="px-6 py-3">
                  <p className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-2">Pick a reaction</p>
                  <div className="grid grid-cols-6 gap-2">
                    {EMOJI_OPTIONS.map(({ emoji, label }) => (
                      <button key={emoji} onClick={() => setSelectedEmoji(emoji)} className={`flex flex-col items-center py-2.5 rounded-xl transition-all duration-150 ${selectedEmoji === emoji ? "bg-green-50 ring-2 ring-green-400 scale-110" : "bg-gray-50 hover:bg-gray-100"}`}>
                        <span className="text-2xl">{emoji}</span>
                        <span className="text-[9px] text-gray-400 mt-0.5">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="px-6 py-3">
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add a thank-you note (optional)" maxLength={150} rows={2} className="w-full p-3 bg-gray-50/80 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent resize-none" />
                  <p className="text-[10px] text-gray-300 text-right mt-0.5">{message.length}/150</p>
                </div>
              </>
            )}

            {showDontAskAgain && !isBoth && (
              <div className="px-6 pb-1">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input type="checkbox" checked={dontAskAgain} onChange={(e) => setDontAskAgain(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-green-500 focus:ring-green-400 cursor-pointer" />
                  <span className="text-xs text-gray-400 group-hover:text-gray-600 transition">Don't ask me again after claiming</span>
                </label>
              </div>
            )}

            <div className="px-6 pb-6 pt-3 flex gap-2">
              <button onClick={handleClose} className="btn-pop flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200">Skip</button>
              <button onClick={handleSend} disabled={sending} className="btn-pop flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-green-500 hover:bg-green-600 shadow-sm disabled:opacity-50">
                {sending ? "Sending..." : isBoth ? "Send Kudos" : `Send ${selectedEmoji}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
